import React from "react";
import { Activity, ArrowDown, ArrowUp, Gauge, Radar, ScatterChart } from "lucide-react";
import { Panel } from "./Panel";

const StatusPill = ({ status }) => {
  const map = {
    NEUTRAL: "bg-amber-500/10 text-amber-400 border-amber-500/25",
    MODERATE: "bg-amber-500/10 text-amber-400 border-amber-500/25",
    "RISK-ON": "bg-emerald-500/10 text-emerald-400 border-emerald-500/25",
    "RISK-OFF": "bg-rose-500/10 text-rose-400 border-rose-500/25",
  };
  return (
    <span className={`rounded border px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide ${map[status] || "bg-zinc-500/10 text-zinc-400 border-zinc-500/25"}`}>
      {status}
    </span>
  );
};

const Meter = ({ value, color }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-800/80">
    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${value}%`, background: color }} />
  </div>
);

export const IntelligencePanels = ({ intel }) => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
    {/* What's Moving */}
    <Panel data-testid="market-intel-whats-moving" hover className="p-4">
      <div className="kicker mb-3 flex items-center gap-1.5"><Activity className="h-3.5 w-3.5 text-amber-400" />{intel.whats_moving.title}</div>
      <div className="space-y-2.5">
        {intel.whats_moving.items.map((it) => (
          <div key={it.label} className="flex items-center justify-between gap-3 border-b border-white/[0.05] pb-2.5 last:border-0 last:pb-0">
            <div className="min-w-0">
              <div className="text-sm font-medium text-white">{it.label}</div>
              <div className="truncate text-xs text-zinc-500">{it.detail}</div>
            </div>
            <span className={`flex shrink-0 items-center gap-1 font-mono text-xs ${it.direction === "up" ? "text-emerald-400" : "text-rose-400"}`}>
              {it.direction === "up" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
              {it.value}
            </span>
          </div>
        ))}
      </div>
    </Panel>

    {/* Momentum */}
    <Panel data-testid="market-intel-momentum" hover className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="kicker flex items-center gap-1.5"><Gauge className="h-3.5 w-3.5 text-amber-400" />{intel.momentum.title}</div>
        <StatusPill status={intel.momentum.status} />
      </div>
      <div className="mb-2 flex items-end justify-between">
        <span className="font-mono text-2xl font-bold text-white tabular-nums">{intel.momentum.reading}</span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">/ 100</span>
      </div>
      <Meter value={intel.momentum.reading} color="#F59E0B" />
      <p className="mt-3 text-xs leading-relaxed text-zinc-400">{intel.momentum.detail}</p>
    </Panel>

    {/* Risk conditions */}
    <Panel data-testid="market-intel-risk-conditions" hover className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="kicker flex items-center gap-1.5"><Radar className="h-3.5 w-3.5 text-amber-400" />{intel.risk_conditions.title}</div>
        <StatusPill status={intel.risk_conditions.status} />
      </div>
      <div className="mb-2 flex items-end justify-between">
        <span className="font-mono text-2xl font-bold text-white tabular-nums">{intel.risk_conditions.reading}</span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">volatility</span>
      </div>
      <Meter value={intel.risk_conditions.reading} color="#0EA5E9" />
      <p className="mt-3 text-xs leading-relaxed text-zinc-400">{intel.risk_conditions.detail}</p>
    </Panel>

    {/* Unusual Activity */}
    <Panel data-testid="market-intel-unusual-activity" hover className="p-4">
      <div className="kicker mb-3 flex items-center gap-1.5"><ScatterChart className="h-3.5 w-3.5 text-amber-400" />{intel.unusual_activity.title}</div>
      <div className="space-y-2.5">
        {intel.unusual_activity.items.map((it) => (
          <div key={it.label} className="rounded-md border border-white/[0.06] bg-[#0B0C0E] p-2.5">
            <div className="flex items-center justify-between">
              <span className="font-mono text-sm font-semibold text-white">{it.label}</span>
              <span className="rounded border border-amber-500/25 bg-amber-500/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wide text-amber-400">{it.tag}</span>
            </div>
            <p className="mt-1 text-xs text-zinc-500">{it.detail}</p>
          </div>
        ))}
      </div>
    </Panel>

    {/* Market Breadth */}
    <Panel data-testid="market-intel-market-breadth" hover className="p-4 md:col-span-2 lg:col-span-2">
      <div className="kicker mb-3 flex items-center gap-1.5"><Activity className="h-3.5 w-3.5 text-amber-400" />{intel.breadth.title}</div>
      <div className="mb-3 flex h-3 overflow-hidden rounded-full border border-white/[0.06]">
        <div className="bg-emerald-500/70" style={{ width: `${(intel.breadth.advancers / 50) * 100}%` }} />
        <div className="bg-zinc-600/60" style={{ width: `${(intel.breadth.unchanged / 50) * 100}%` }} />
        <div className="bg-rose-500/70" style={{ width: `${(intel.breadth.decliners / 50) * 100}%` }} />
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-emerald-500" /><span className="text-xs text-zinc-400">Advancing</span><span className="font-mono text-sm font-semibold text-emerald-400">{intel.breadth.advancers}</span></div>
        <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-zinc-500" /><span className="text-xs text-zinc-400">Unchanged</span><span className="font-mono text-sm font-semibold text-zinc-300">{intel.breadth.unchanged}</span></div>
        <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-rose-500" /><span className="text-xs text-zinc-400">Declining</span><span className="font-mono text-sm font-semibold text-rose-400">{intel.breadth.decliners}</span></div>
      </div>
      <p className="mt-3 text-xs leading-relaxed text-zinc-400">{intel.breadth.detail}</p>
    </Panel>
  </div>
);
